import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Activity, Battery, Droplet, Shield, Heart, Globe, Scale, Leaf, ArrowRight, Award, Zap, Eye } from 'lucide-react';

export default function Health() {
  const { t, language } = useLanguage();

  const assets = {
    chru1: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/kaitr28n_image%20CHRu%201.png',
    chru2: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/9mcfnvna_Image%20CHRu%202.png',
    splitPayment: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/a86ae6ce1c391b744544e7162e0adf4bc6bfea1538c43bb1958dfd934f9f0a87.png',
    picsuArch: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/c49f2b00dbc43f29fb906612a37817f96fec32a585bcb95e8004ad1985f5f4a8.png',
  };

  // Message humaniste principal - EXACTEMENT comme demandé
  const humanistMessage = {
    fr: {
      picsuFull: "PICSU (Programme d'Implémentation de la Couverture Santé Universelle)",
      mainMessage: "Le PICSU (Programme d'Implémentation de la Couverture Santé Universelle) est une réponse humaniste à l'injustice sociale. Nous croyons que la santé est un droit universel inaliénable. Ce système brise les barrières financières pour garantir un accès équitable aux soins de qualité pour tous, rendant ainsi aux Nations leur souveraineté sanitaire et à chaque citoyen sa dignité.",
      superiorityTitle: "Supériorité Technique Mondiale",
      superiorityText: "Le PICSU (Programme d'Implémentation de la Couverture Santé Universelle) est le seul système au monde à combiner l'architecture \"Argent-Flux\" (liquidation instantanée), la Médecine Traditionnelle homologuée et une traçabilité totale contre la corruption, surpassant ainsi les modèles classiques comme le NHS britannique.",
      dignityTitle: "Le Droit à la Dignité",
      dignityText: "Le PICSU (Programme d'Implémentation de la Couverture Santé Universelle) n'est pas qu'une solution technologique ; c'est un bouclier contre l'injustice. Notre mission est d'éliminer la barrière financière pour que la santé ne soit plus un privilège, mais un droit universel et équitable.",
      justiceTitle: "Justice Sociale",
      justiceText: "Le PICSU (Programme d'Implémentation de la Couverture Santé Universelle) est le seul système capable d'offrir la même qualité de soins à un enfant dans un village reculé qu'à un citoyen de la capitale. C'est la promesse d'une Afrique où la géographie ne détermine plus le destin médical.",
      allianceTitle: "L'Alliance Unique",
      allianceText: "Le PICSU (Programme d'Implémentation de la Couverture Santé Universelle) est le premier système mondial à intégrer de façon intelligente et scientifique la Médecine Traditionnelle, offrant une souveraineté thérapeutique totale.",
      splitPaymentTitle: "Split Payment - Transparence Fiduciaire Totale",
      features: ["Architecture Argent-Flux", "Liquidation Instantanée", "Médecine Traditionnelle Homologuée", "Traçabilité Anti-Corruption"]
    },
    en: {
      picsuFull: "PICSU (Universal Health Coverage Implementation Program)",
      mainMessage: "PICSU (Universal Health Coverage Implementation Program) is a humanist response to social injustice. We believe that health is an inalienable universal right. This system breaks down financial barriers to guarantee equitable access to quality care for all, thus restoring to Nations their health sovereignty and to each citizen their dignity.",
      superiorityTitle: "Global Technical Superiority",
      superiorityText: "PICSU (Universal Health Coverage Implementation Program) is the only system in the world to combine the \"Money-Flow\" architecture (instant settlement), certified Traditional Medicine and total traceability against corruption, thus surpassing classic models like the British NHS.",
      dignityTitle: "The Right to Dignity",
      dignityText: "PICSU (Universal Health Coverage Implementation Program) is not just a technological solution; it's a shield against injustice. Our mission is to eliminate the financial barrier so that health is no longer a privilege, but a universal and equitable right.",
      justiceTitle: "Social Justice",
      justiceText: "PICSU (Universal Health Coverage Implementation Program) is the only system capable of providing the same quality of care to a child in a remote village as to a citizen of the capital. This is the promise of an Africa where geography no longer determines medical destiny.",
      allianceTitle: "The Unique Alliance",
      allianceText: "PICSU (Universal Health Coverage Implementation Program) is the first global system to intelligently and scientifically integrate Traditional Medicine, offering total therapeutic sovereignty.",
      splitPaymentTitle: "Split Payment - Total Fiduciary Transparency",
      features: ["Money-Flow Architecture", "Instant Settlement", "Certified Traditional Medicine", "Anti-Corruption Traceability"]
    },
    pt: {
      picsuFull: "PICSU (Programa de Implementação da Cobertura Universal de Saúde)",
      mainMessage: "O PICSU (Programa de Implementação da Cobertura Universal de Saúde) é uma resposta humanista à injustiça social. Acreditamos que a saúde é um direito universal inalienável. Este sistema quebra as barreiras financeiras para garantir acesso equitativo a cuidados de qualidade para todos, devolvendo assim às Nações a sua soberania sanitária e a cada cidadão a sua dignidade.",
      superiorityTitle: "Superioridade Técnica Mundial",
      superiorityText: "O PICSU (Programa de Implementação da Cobertura Universal de Saúde) é o único sistema no mundo a combinar a arquitetura \"Fluxo-Dinheiro\" (liquidação instantânea), a Medicina Tradicional homologada e uma rastreabilidade total contra a corrupção, superando assim modelos clássicos como o NHS britânico.",
      dignityTitle: "O Direito à Dignidade",
      dignityText: "O PICSU (Programa de Implementação da Cobertura Universal de Saúde) não é apenas uma solução tecnológica; é um escudo contra a injustiça. Nossa missão é eliminar a barreira financeira para que a saúde deixe de ser um privilégio e se torne um direito universal e equitativo.",
      justiceTitle: "Justiça Social",
      justiceText: "O PICSU (Programa de Implementação da Cobertura Universal de Saúde) é o único sistema capaz de oferecer a mesma qualidade de cuidados a uma criança numa aldeia remota como a um cidadão da capital. Esta é a promessa de uma África onde a geografia não determina mais o destino médico.",
      allianceTitle: "A Aliança Única",
      allianceText: "O PICSU (Programa de Implementação da Cobertura Universal de Saúde) é o primeiro sistema mundial a integrar de forma inteligente e científica a Medicina Tradicional, oferecendo uma soberania terapêutica total.",
      splitPaymentTitle: "Split Payment - Transparência Fiduciária Total",
      features: ["Arquitetura Fluxo-Dinheiro", "Liquidação Instantânea", "Medicina Tradicional Homologada", "Rastreabilidade Anti-Corrupção"]
    },
    it: {
      picsuFull: "PICSU (Programma di Implementazione della Copertura Sanitaria Universale)",
      mainMessage: "Il PICSU (Programma di Implementazione della Copertura Sanitaria Universale) è una risposta umanista all'ingiustizia sociale. Crediamo che la salute sia un diritto universale inalienabile. Questo sistema abbatte le barriere finanziarie per garantire un accesso equo a cure di qualità per tutti, restituendo così alle Nazioni la loro sovranità sanitaria e a ogni cittadino la sua dignità.",
      superiorityTitle: "Superiorità Tecnica Mondiale",
      superiorityText: "Il PICSU (Programma di Implementazione della Copertura Sanitaria Universale) è l'unico sistema al mondo a combinare l'architettura \"Flusso-Denaro\" (liquidazione istantanea), la Medicina Tradizionale omologata e una tracciabilità totale contro la corruzione, superando così i modelli classici come il NHS britannico.",
      dignityTitle: "Il Diritto alla Dignità",
      dignityText: "Il PICSU (Programma di Implementazione della Copertura Sanitaria Universale) non è solo una soluzione tecnologica; è uno scudo contro l'ingiustizia. La nostra missione è eliminare la barriera finanziaria affinché la salute non sia più un privilegio, ma un diritto universale ed equo.",
      justiceTitle: "Giustizia Sociale",
      justiceText: "Il PICSU (Programma di Implementazione della Copertura Sanitaria Universale) è l'unico sistema in grado di offrire la stessa qualità di cure a un bambino in un villaggio remoto come a un cittadino della capitale. Questa è la promessa di un'Africa dove la geografia non determina più il destino medico.",
      allianceTitle: "L'Alleanza Unica",
      allianceText: "Il PICSU (Programma di Implementazione della Copertura Sanitaria Universale) è il primo sistema mondiale a integrare in modo intelligente e scientifico la Medicina Tradizionale, offrendo una sovranità terapeutica totale.",
      splitPaymentTitle: "Split Payment - Trasparenza Fiduciaria Totale",
      features: ["Architettura Flusso-Denaro", "Liquidazione Istantanea", "Medicina Tradizionale Omologata", "Tracciabilità Anti-Corruzione"]
    },
    ru: {
      picsuFull: "PICSU (Программа внедрения всеобщего охвата услугами здравоохранения)",
      mainMessage: "PICSU (Программа внедрения всеобщего охвата услугами здравоохранения) — это гуманистический ответ на социальную несправедливость. Мы верим, что здоровье — это неотъемлемое универсальное право. Эта система разрушает финансовые барьеры, чтобы гарантировать справедливый доступ к качественной медицинской помощи для всех, возвращая таким образом Нациям их суверенитет в сфере здравоохранения, а каждому гражданину — его достоинство.",
      superiorityTitle: "Мировое техническое превосходство",
      superiorityText: "PICSU (Программа внедрения всеобщего охвата услугами здравоохранения) — единственная в мире система, сочетающая архитектуру «Денежный поток» (мгновенная ликвидация), сертифицированную традиционную медицину и полную прослеживаемость против коррупции, превосходя таким образом классические модели, такие как британская NHS.",
      dignityTitle: "Право на достоинство",
      dignityText: "PICSU (Программа внедрения всеобщего охвата услугами здравоохранения) — это не просто технологическое решение; это щит против несправедливости. Наша миссия — устранить финансовый барьер, чтобы здоровье перестало быть привилегией и стало всеобщим и справедливым правом.",
      justiceTitle: "Социальная справедливость",
      justiceText: "PICSU (Программа внедрения всеобщего охвата услугами здравоохранения) — единственная система, способная обеспечить одинаковое качество медицинской помощи ребёнку в отдалённой деревне и жителю столицы. Это обещание Африки, где география больше не определяет медицинскую судьбу.",
      allianceTitle: "Уникальный альянс",
      allianceText: "PICSU (Программа внедрения всеобщего охвата услугами здравоохранения) — первая в мире система, интеллектуально и научно интегрирующая традиционную медицину, обеспечивая полный терапевтический суверенитет.",
      splitPaymentTitle: "Split Payment - Полная фидуциарная прозрачность",
      features: ["Архитектура Денежный Поток", "Мгновенная Ликвидация", "Сертифицированная Традиционная Медицина", "Антикоррупционная Прослеживаемость"]
    },
    zh: {
      picsuFull: "PICSU（全民健康覆盖实施计划）",
      mainMessage: "PICSU（全民健康覆盖实施计划）是对社会不公正的人道主义回应。我们相信健康是不可剥夺的普遍权利。该系统打破财务障碍，确保所有人都能公平获得优质医疗服务，从而恢复各国的卫生主权和每位公民的尊严。",
      superiorityTitle: "全球技术领先",
      superiorityText: "PICSU（全民健康覆盖实施计划）是世界上唯一将「资金流」架构（即时结算）、认证传统医学和完全反腐败可追溯性相结合的系统，超越了英国NHS等传统模式。",
      dignityTitle: "尊严权利",
      dignityText: "PICSU（全民健康覆盖实施计划）不仅是一个技术解决方案；它是抵御不公正的盾牌。我们的使命是消除财务障碍，使健康不再是特权，而是普遍公平的权利。",
      justiceTitle: "社会正义",
      justiceText: "PICSU（全民健康覆盖实施计划）是唯一能够为偏远村庄的儿童提供与首都市民同等质量医疗服务的系统。这是对非洲的承诺：地理不再决定医疗命运。",
      allianceTitle: "独特联盟",
      allianceText: "PICSU（全民健康覆盖实施计划）是全球首个智能科学整合传统医学的系统，提供完全的治疗主权。",
      splitPaymentTitle: "Split Payment - 完全信托透明",
      features: ["资金流架构", "即时结算", "认证传统医学", "反腐败可追溯性"]
    }
  };

  const c = humanistMessage[language] || humanistMessage.fr;

  return (
    <div className="min-h-screen bg-white pt-32">
      {/* HERO - Message Humaniste Principal */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-sky-900 via-slate-800 to-emerald-900 text-white overflow-hidden" data-testid="health-hero">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="inline-block px-4 py-2 bg-sky-600/20 border border-sky-400/30 rounded-full backdrop-blur-sm mb-6">
            <p className="text-sm font-medium text-sky-300 uppercase tracking-wider">
              {t('health.subtitle')}
            </p>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {t('health.title')}
          </h1>
          
          {/* PICSU Definition - Encadré */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-8 max-w-4xl">
            <p className="text-xl md:text-2xl font-semibold text-sky-300">
              {c.picsuFull}
            </p>
          </div>
        </div>
      </section>

      {/* MESSAGE HUMANISTE PRINCIPAL - En tête de section */}
      <section className="py-16 bg-gradient-to-b from-red-50 to-white" data-testid="humanist-main-message">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl border-2 border-red-200 p-10 shadow-xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
                {language === 'fr' ? 'Notre Engagement Humaniste' : 
                 language === 'en' ? 'Our Humanist Commitment' :
                 language === 'pt' ? 'Nosso Compromisso Humanista' :
                 language === 'it' ? 'Il Nostro Impegno Umanista' :
                 language === 'ru' ? 'Наша гуманистическая миссия' :
                 '我们的人道主义承诺'}
              </h2>
            </div>
            <p className="text-lg md:text-xl text-slate-700 leading-relaxed font-medium">
              {c.mainMessage}
            </p>
          </div>
        </div>
      </section>

      {/* SUPÉRIORITÉ TECHNIQUE MONDIALE */}
      <section className="py-20 bg-slate-900 text-white" data-testid="superiority-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-amber-400" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
              {c.superiorityTitle}
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-4xl mx-auto">
              {c.superiorityText}
            </p>
          </div>

          {/* 4 Piliers de la Supériorité */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {c.features.map((feature, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                <div className="w-12 h-12 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  {i === 0 && <Zap className="w-6 h-6 text-sky-400" />}
                  {i === 1 && <Activity className="w-6 h-6 text-emerald-400" />}
                  {i === 2 && <Leaf className="w-6 h-6 text-green-400" />}
                  {i === 3 && <Eye className="w-6 h-6 text-amber-400" />}
                </div>
                <p className="text-sm font-semibold text-white">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dignité & Justice */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="dignity-justice-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Le Droit à la Dignité */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                {c.dignityTitle}
              </h2>
              <p className="text-base text-slate-700 leading-relaxed">
                {c.dignityText}
              </p>
            </div>

            {/* Justice Sociale */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <Scale className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                {c.justiceTitle}
              </h2>
              <p className="text-base text-slate-700 leading-relaxed">
                {c.justiceText}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* L'Alliance Unique */}
      <section className="py-16 bg-emerald-900 text-white" data-testid="alliance-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-emerald-600/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Leaf className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                  {c.allianceTitle}
                </h3>
                <p className="text-base text-slate-300 leading-relaxed">
                  {c.allianceText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PICSU Section with Definition */}
      <section className="py-16" data-testid="picsu-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-6">
                <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider">
                  {t('health.picsu.subtitle')}
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                {t('health.picsu.title')}
              </h2>
              <p className="text-sm text-sky-600 font-medium mb-6 bg-sky-50 px-4 py-2 rounded-lg inline-block">
                {c.picsuFull}
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t('health.picsu.description')}
              </p>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                {t('health.picsu.features')}
              </h3>
              <ul className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-sky-600"></div>
                    </div>
                    <span className="text-base text-slate-700">
                      {t(`health.picsu.feature${i}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <img src={assets.picsuArch} alt="PICSU Architecture" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Split Payment Section */}
      <section className="py-16 bg-slate-50" data-testid="split-payment-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white rounded-3xl border border-slate-200 p-8 md:p-12 shadow-lg">
            <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-6 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
              {c.splitPaymentTitle}
            </h3>
            <div className="rounded-xl overflow-hidden">
              <img src={assets.splitPayment} alt="Split Payment Diagram" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* CHRu Section */}
      <section className="py-16" data-testid="chru-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-1 gap-4">
                <img src={assets.chru1} alt="CHRu Exterior" className="w-full rounded-2xl border border-slate-200 shadow-lg" />
                <img src={assets.chru2} alt="CHRu Aerial View" className="w-full rounded-2xl border border-slate-200 shadow-lg" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-6">
                <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
                  {t('health.chru.subtitle')}
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
                {t('health.chru.title')}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t('health.chru.description')}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Battery, text: t('health.chru.feature1') },
                  { icon: Droplet, text: t('health.chru.feature2') },
                  { icon: Activity, text: t('health.chru.feature3') },
                  { icon: Shield, text: t('health.chru.feature4') },
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <feature.icon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                    <span className="text-sm text-slate-700">
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PNS Section */}
      <section className="py-16 bg-slate-900 text-white" data-testid="pns-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
              {t('health.pns.title')}
            </h2>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              {t('health.pns.description')}
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-semibold mb-6">
              {t('health.council.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-sky-400"></div>
                  <span className="text-base">
                    {t(`health.council.member${i}`)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-slate-400">
                {t('health.pns.partner')}: <span className="text-white font-medium">{t('health.pns.partnerName')}</span>
              </p>
              <Link
                to="/scientific-council"
                className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-full font-medium transition-colors"
              >
                {language === 'fr' ? 'Voir le Conseil Scientifique' : 
                 language === 'en' ? 'View Scientific Council' :
                 language === 'pt' ? 'Ver Conselho Científico' :
                 language === 'it' ? 'Vedi Consiglio Scientifico' :
                 language === 'ru' ? 'Посмотреть Научный Совет' :
                 '查看科学委员会'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
